import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    DeleteOutline as DeleteIcon,
    ArrowUpward as UpIcon,
    ArrowDownward as DownIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

import {
    loadWsPresets,
    saveWsPresets,
    CHANGE_EVENT,
} from './connection_presets';

const validateHost = (value) => {
    if (!value) return { valid: false, reason: 'Host is required' };
    if (/\s/.test(value)) return { valid: false, reason: 'No whitespace allowed' };
    if (/^wss?:\/\//i.test(value)) return { valid: false, reason: 'Do not include ws:// or wss://' };
    const m = value.match(/^([^:/?#]+)(?::(\d+))?(\/.*)?$/);
    if (!m) return { valid: false, reason: 'Invalid host' };
    return { valid: true };
};

const ConnectionSettings = () => {
    const [presets, setPresets] = useState(() => loadWsPresets());
    const [visibleSigning, setVisibleSigning] = useState({});

    useEffect(() => {
        const onChange = (e) => {
            const next = (e && e.detail && e.detail.presets) || loadWsPresets();
            setPresets(next);
        };
        window.addEventListener(CHANGE_EVENT, onChange);
        return () => window.removeEventListener(CHANGE_EVENT, onChange);
    }, []);

    const persist = (next) => {
        const saved = saveWsPresets(next);
        setPresets(saved);
    };

    const updateField = (id, field, value) => {
        persist(presets.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
    };

    const remove = (id) => {
        persist(presets.filter((p) => p.id !== id));
    };

    const move = (id, delta) => {
        const idx = presets.findIndex((p) => p.id === id);
        if (idx < 0) return;
        const target = idx + delta;
        if (target < 0 || target >= presets.length) return;
        const next = presets.slice();
        const [item] = next.splice(idx, 1);
        next.splice(target, 0, item);
        persist(next);
    };

    const add = () => {
        const id = `preset-${Date.now()}`;
        persist([...presets, { id, label: 'New Preset', scheme: 'ws', host: '127.0.0.1:5555' }]);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    WebSocket Connections
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                    These appear in the Connection panel's URL dropdown.
                </Typography>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={add}>
                    Add Preset
                </Button>
            </Stack>

            <Stack spacing={1}>
                {presets.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No presets yet. Click <b>Add Preset</b> to create one.
                    </Typography>
                )}
                {presets.map((p, i) => {
                    const v = validateHost(p.host);
                    const showSigning = !!visibleSigning[p.id];
                    return (
                        <Paper key={p.id} variant="outlined" sx={{ px: 1, py: 0.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <TextField
                                    size="small"
                                    label="Label"
                                    value={p.label}
                                    onChange={(e) => updateField(p.id, 'label', e.target.value)}
                                    sx={{ width: 180 }}
                                />
                                <FormControl size="small" sx={{ width: 100 }}>
                                    <InputLabel id={`scheme-label-${p.id}`}>Scheme</InputLabel>
                                    <Select
                                        labelId={`scheme-label-${p.id}`}
                                        label="Scheme"
                                        value={p.scheme === 'wss' ? 'wss' : 'ws'}
                                        onChange={(e) => updateField(p.id, 'scheme', e.target.value)}
                                    >
                                        <MenuItem value="ws">ws://</MenuItem>
                                        <MenuItem value="wss">wss://</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Host"
                                    placeholder="host:port"
                                    value={p.host || ''}
                                    onChange={(e) => updateField(p.id, 'host', e.target.value)}
                                    error={(p.host || '').length > 0 && !v.valid}
                                    helperText={(p.host || '').length > 0 && !v.valid ? v.reason : ''}
                                    FormHelperTextProps={{ sx: { m: 0, mt: 0.25, lineHeight: 1 } }}
                                    sx={{ flex: 1, minWidth: 180 }}
                                />
                                <TextField
                                    size="small"
                                    label="Signing"
                                    placeholder="Optional"
                                    value={p.signingKey || ''}
                                    onChange={(e) => updateField(p.id, 'signingKey', e.target.value)}
                                    type={showSigning ? 'text' : 'password'}
                                    sx={{ flex: 1, minWidth: 180 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={showSigning ? 'Hide' : 'Show'}>
                                                    <IconButton
                                                        size="small"
                                                        edge="end"
                                                        onClick={() => setVisibleSigning((s) => ({ ...s, [p.id]: !s[p.id] }))}
                                                    >
                                                        {showSigning ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Stack direction="row" alignItems="center">
                                    <Tooltip title="Move up">
                                        <span>
                                            <IconButton size="small" onClick={() => move(p.id, -1)} disabled={i === 0}>
                                                <UpIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Move down">
                                        <span>
                                            <IconButton size="small" onClick={() => move(p.id, 1)} disabled={i === presets.length - 1}>
                                                <DownIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" color="error" onClick={() => remove(p.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default ConnectionSettings;
